<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  pricingLimits: {
    type: Object,
    required: true
  },
  initialPricing: {
    type: Object,
    default: () => ({})
  },
  hasGoldMethods: {
    type: Boolean,
    default: false
  },
  errors: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:pricing'])

/**
 * Five pence.
 *
 * It was a penny, which made both ends of the range reachable but put roughly
 * ten thousand stops between them: landing on £50 exactly was luck.
 *
 * The step alone is not enough. A slider steps from its **floor**, so with a
 * floor of £0.99 every stop lands at x.99 or x.04 and no whole pound is
 * reachable at all. The floor is therefore snapped up onto the same grid, and
 * the ceiling down onto it, so £1.00, £50.00 and £100.00 are all stops.
 */
const PRICE_STEP = 0.05

/** Rounded to the nearest penny: 0.1 + 0.2 is not 0.3 in binary floating point. */
const toGrid = (value, direction) => {
  const steps = direction === 'up'
    ? Math.ceil(value / PRICE_STEP)
    : Math.floor(value / PRICE_STEP)

  return Math.round(steps * PRICE_STEP * 100) / 100
}

/**
 * What the slider is allowed to offer.
 *
 * These bounds used to be inlined at a dozen call sites as `|| 20`, `|| 80`,
 * `|| 150` — each one a copy of a floor the platform no longer has, and each
 * free to contradict the setting it was standing in for. They are resolved once
 * here, and the fallbacks match what the settings actually hold.
 */
const limits = computed(() => ({
  min: toGrid(props.pricingLimits?.gold?.min ?? 0.99, 'up'),
  average: props.pricingLimits?.gold?.average ?? 50,
  max: toGrid(props.pricingLimits?.gold?.max ?? 100, 'down'),
}))

const formatPrice = (value) => Number(value ?? 0).toFixed(2)

/**
 * A price saved before these bounds changed is brought inside them.
 *
 * A draft holding a figure from when the ceiling was lower left the slider
 * pinned at the maximum while the number beside it still read the old price —
 * and the save was then rejected by the same bounds the slider is drawn from.
 * Clamping on the way in keeps the two in agreement and the submission valid.
 *
 * It is snapped onto the step grid for the same reason: a browser silently
 * moves a range input to the nearest valid stop, so a stored price off the grid
 * would be shown by the slider and by the figure beside it as two different
 * numbers. Every price on record is already a multiple of five pence.
 */
const clampPrice = (value) => {
  const { min, max } = limits.value
  const inRange = Math.min(Math.max(Number(value), min), max)

  return Math.min(Math.max(toGrid(inRange, 'down'), min), max)
}

const goldPrice = ref(
  (props.initialPricing?.gold && props.initialPricing.gold > 0)
    ? clampPrice(props.initialPricing.gold)
    : limits.value.min
)

watch(() => props.initialPricing, (newPricing) => {
  goldPrice.value = newPricing?.gold > 0 ? clampPrice(newPricing.gold) : limits.value.min
}, { deep: true })

const fillPercent = computed(() => {
  const { min, max } = limits.value
  const span = max - min

  return span > 0 ? ((goldPrice.value - min) / span) * 100 : 0
})

/**
 * The slider warns once a price is above the recommended figure.
 *
 * The two colours were a hardcoded amber and a hardcoded blue, neither of which
 * is in the palette — the blue in particular read as a stray browser default
 * beside the brand teal everywhere else on the page.
 */
const sliderColor = computed(() =>
  goldPrice.value > limits.value.average ? 'accent-warning' : 'accent-brand'
)

const gradientColor = computed(() =>
  goldPrice.value > limits.value.average ? 'var(--warning)' : 'var(--brand)'
)

/**
 * Emitted immediately as well as on change, so a price clamped into range on
 * the way in reaches the step that submits it instead of leaving the old,
 * out-of-range figure there to be rejected.
 */
watch(goldPrice, () => {
  emit('update:pricing', { gold: goldPrice.value })
}, { immediate: true })
</script>

<template>
  <!--
    One card, because Bindle sells one thing. There were three: a content
    library on its own and a group class alongside this, neither of which ever
    sold, and both of which asked a tutor to price something no visitor could
    buy.
  -->
  <div class="mb-8 grid grid-cols-1 gap-6 md:mx-auto md:max-w-xl">
    <section id="gold-plan-pricing">
      <div
        class="flex h-full flex-col rounded-2xl border-2 p-8 transition-all"
        :class="hasGoldMethods
          ? 'border-line-brand bg-surface hover:border-brand hover:shadow-lg'
          : 'border-line bg-surface-2 opacity-60'"
      >
        <div class="mb-6 flex items-center gap-4">
          <div class="grid size-16 shrink-0 place-items-center rounded-2xl bg-brand-soft">
            <span class="material-symbols-outlined text-3xl text-brand">workspace_premium</span>
          </div>
          <div class="min-w-0">
            <h3 class="text-xl font-bold text-ink">1:1 Tutoring</h3>
            <p class="text-sm text-muted">A weekly one-to-one lesson + your content library</p>
          </div>
        </div>

        <div class="mb-8 space-y-3">
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-success">check</span>
            <span class="text-sm font-semibold text-ink">Weekly 1:1 sessions</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-success">check</span>
            <span class="text-sm text-muted">Your videos and study material</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-success">check</span>
            <span class="text-sm text-muted">Direct messaging with tutor</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-success">check</span>
            <span class="text-sm text-muted">Personalized study plans</span>
          </div>
        </div>

        <div class="flex-grow"></div>

        <div v-if="!hasGoldMethods" class="mt-6 rounded-xl border border-warning bg-warning-bg p-4">
          <div class="flex items-center gap-2 text-warning">
            <span class="material-symbols-outlined text-lg">info</span>
            <p class="text-sm font-medium">You do not offer this service</p>
          </div>
          <p class="mt-1 text-xs text-warning">Go back to Services Offered and select 1:1 tutoring to set a price</p>
        </div>

        <div v-else class="pricing-control mt-6">
          <div class="mb-4 flex items-center justify-between">
            <span class="text-sm font-semibold text-ink">Weekly Price</span>
            <span class="rounded-full bg-surface-2 px-3 py-1 text-xs text-muted">
              Recommended: £{{ formatPrice(limits.average) }}
            </span>
          </div>
          <div class="relative mb-6">
            <input
              v-model.number="goldPrice"
              type="range"
              :min="limits.min"
              :max="limits.max"
              :step="PRICE_STEP"
              class="slider h-3 w-full cursor-pointer appearance-none rounded-lg"
              :class="sliderColor"
              :style="{
                background: `linear-gradient(to right,
                  ${gradientColor} 0%,
                  ${gradientColor} ${fillPercent}%,
                  var(--line-strong) ${fillPercent}%,
                  var(--line-strong) 100%)`
              }"
            >
            <div class="mt-2 flex justify-between text-xs text-muted">
              <span>£{{ formatPrice(limits.min) }}</span>
              <span>£{{ formatPrice(limits.max) }}</span>
            </div>
          </div>
          <div class="rounded-xl bg-brand-soft py-6 text-center">
            <span class="text-4xl font-bold text-ink">£{{ formatPrice(goldPrice) }}</span>
            <span class="text-lg font-bold text-muted"> weekly subscription</span>
          </div>
          <div class="mt-3 min-h-[3rem]">
            <div v-if="errors['pricing.gold']" class="rounded-lg border border-red-200 bg-red-50 p-3">
              <div class="flex items-start gap-2">
                <span class="material-symbols-outlined text-sm text-red-600">error</span>
                <p class="text-sm text-red-700">{{ errors['pricing.gold'] }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
/* Custom slider styling */
input[type="range"].slider {
  -webkit-appearance: none;
  appearance: none;
  height: 12px;
  border-radius: 6px;
  outline: none;
}

input[type="range"].slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: white;
  border: 3px solid currentColor;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease;
}

input[type="range"].slider::-webkit-slider-thumb:hover {
  transform: scale(1.1);
}

input[type="range"].slider::-moz-range-thumb {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: white;
  border: 3px solid currentColor;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease;
}

input[type="range"].slider::-moz-range-thumb:hover {
  transform: scale(1.1);
}

input[type="range"].slider::-moz-range-track {
  height: 12px;
  border-radius: 6px;
}

.accent-brand {
  color: var(--brand);
}

.accent-warning {
  color: var(--warning);
}
</style>
